import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Paper
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { sprintService } from '../services/api';

function BurndownChart({ sprintId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sprintId) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await sprintService.getBurndown(sprintId);
        setData(res.data);
      } catch {
        setError('Impossible de charger les données du burndown');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sprintId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={32} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data || !data.data?.length) return <Alert severity="info">Aucune donnée disponible. Assignez des story points aux tâches du sprint.</Alert>;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" color="primary">{data.totalPoints}</Typography>
          <Typography variant="caption" color="text.secondary">Total points</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" color="success.main">
            {data.totalPoints - (data.data[data.data.length - 1]?.remaining || 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">Points terminés</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" color="warning.main">
            {data.data[data.data.length - 1]?.remaining || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">Points restants</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value, name) => [
              `${value} pts`,
              name === 'remaining' ? 'Restant réel' : 'Idéal'
            ]}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Legend
            formatter={(value) => value === 'remaining' ? 'Restant réel' : 'Ligne idéale'}
          />
          <Line
            type="monotone"
            dataKey="ideal"
            stroke="#94a3b8"
            strokeDasharray="5 5"
            dot={false}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="remaining"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default BurndownChart;
